import type { User } from "firebase/auth";
import { Fragment, type RefObject, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { ACCEPT, appliedText, NO_PLAN_LINK, openAccounts, checkText, cornersText, parseShapeFiles, PREVIEW_COLOUR, type ReadFile, RESULT_TEXT, toWrite } from "./areaImport";
import { pathsOf } from "./areaMap";
import { go } from "./hooks";
import type { GoogleMap, Maps } from "./maps";
import { saveFailure } from "./save";

type Step = "pick" | "checking" | "checked" | "asking" | "applying" | "done";

const readAll = (list: FileList | null): Promise<ReadFile[]> =>
  Promise.all(Array.from(list ?? []).map(async (file) => ({ name: file.name, size: file.size, text: file.size > 1_000_000 ? "" : await file.text() })));

/** Rule V-20: import the area shapes from GeoJSON files, check them, show them on the map, then keep them. */
export function AreaShapeImport({
  maps,
  map,
  user,
  base,
  onApplied,
  onUnavailable,
  onClose,
}: {
  maps: Maps;
  /** The organization path, for the link to the accounts with no area. */
  base?: string;
  map: RefObject<GoogleMap | null>;
  user: User;
  onApplied: () => void;
  /** The API is older than this import (the route is not there). */
  onUnavailable: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("pick");
  const [files, setFiles] = useState<api.ShapeFile[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [useColour, setUseColour] = useState(false);
  const [reply, setReply] = useState<api.ShapeImportReply | null>(null);
  const [failure, setFailure] = useState("");
  const status = useRef<HTMLParagraphElement>(null);
  const applyButton = useRef<HTMLButtonElement>(null);
  const busy = step === "checking" || step === "applying";

  const shown = step === "done" ? [] : toWrite(reply?.items ?? []);
  const shownKey = JSON.stringify(shown.map((item) => item.area_id));
  useEffect(() => {
    const view = map.current;
    if (!view || !shown.length) return;
    const bounds = new maps.maps.LatLngBounds();
    const drawn = shown.flatMap((item) => {
      const paths = pathsOf(item.polygon);
      for (const point of paths[0] ?? []) bounds.extend(point);
      if (!paths.length) return [];
      return [
        new maps.maps.Polygon({
          map: view,
          paths,
          clickable: false,
          strokeColor: PREVIEW_COLOUR,
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: PREVIEW_COLOUR,
          fillOpacity: 0.12,
          zIndex: 3,
        }),
      ];
    });
    if (drawn.length) view.fitBounds(bounds, 48);
    return () => {
      for (const shape of drawn) shape.setMap(null);
    };
  }, [maps, map, shownKey]);

  const send = async (picked: api.ShapeFile[], dryRun: boolean, colour: boolean) => {
    const started = performance.now();
    setStep(dryRun ? "checking" : "applying");
    setFailure("");
    try {
      const found = await api.importAreaShapes(await user.getIdToken(), picked, dryRun, colour);
      console.info(`shape import: ${dryRun ? "dry run" : "apply"} of ${picked.length} files, ${found.items.length} items in ${Math.round(performance.now() - started)} ms`);
      setReply(found);
      setStep(dryRun ? "checked" : "done");
      if (!dryRun) onApplied();
    } catch (e) {
      console.warn(`shape import: ${dryRun ? "dry run" : "apply"} failed`, e instanceof api.ApiError ? e.status : e);
      if (e instanceof api.ApiError && e.status === 404) {
        onUnavailable();
        return;
      }
      setFailure(e instanceof api.ApiError && e.status === 413 && e.detail ? e.detail : saveFailure(e, "The import").text);
      setStep(dryRun ? "pick" : "checked");
    }
    requestAnimationFrame(() => status.current?.focus());
  };

  const pick = async (list: FileList | null) => {
    const parsed = parseShapeFiles(await readAll(list));
    setFiles(parsed.files);
    setProblems(parsed.problems);
    setReply(null);
    if (parsed.files.length) await send(parsed.files, true, useColour);
    else setStep("pick");
  };
  const pickColour = (next: boolean) => {
    setUseColour(next);
    if (files.length && step !== "done") void send(files, true, next);
  };
  const writes = toWrite(reply?.items ?? []).length;

  return (
    <section className="area-import" data-testid="area-import" aria-labelledby="area-import-title">
      <h3 id="area-import-title">Import shapes</h3>
      <p className="sub">
        Pick one or more GeoJSON files, for example the territories from Mapsly. The name of each shape must be an area code, like N1.01. The import adds no area.
      </p>
      <div className="pin-map-bar">
        <label htmlFor="area-import-file">GeoJSON files</label>
        <input
          id="area-import-file"
          data-testid="area-import-file"
          type="file"
          multiple
          accept={ACCEPT}
          disabled={busy}
          onChange={(e) => void pick(e.target.files)}
        />
        <label className="area-import-colour">
          <input type="checkbox" data-testid="area-import-colour" checked={useColour} disabled={busy} onChange={(e) => pickColour(e.target.checked)} />
          <span>Use the colours of the files</span>
        </label>
      </div>
      {problems.length > 0 && (
        <ul className="error" role="alert" data-testid="area-import-problems">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
      <p ref={status} tabIndex={-1} role="status" className={failure ? "error" : "sub"} data-testid="area-import-status">
        {failure ||
          (step === "checking"
            ? "Checking the files…"
            : step === "applying"
              ? "Saving the shapes…"
              : reply && step === "done"
                ? appliedText(reply)
                : reply
                  ? `${checkText(reply)}${writes ? " The new shapes are on the map in pink." : ""}`
                  : "")}
        {reply && step === "done" && base !== undefined && openAccounts(reply) > 0 && (
          <>
            {" "}
            <a href={`${base}${NO_PLAN_LINK}`} onClick={go} data-testid="area-import-no-area">
              See the accounts with no service plan
            </a>
          </>
        )}
      </p>
      {reply && <ShapeImportTable items={reply.items} />}
      <div className="actions">
        {reply && step !== "done" && (
          <button
            type="button"
            ref={applyButton}
            className="primary"
            data-testid="area-import-apply"
            disabled={busy || !writes || step === "asking"}
            onClick={() => setStep("asking")}
          >
            {step === "applying" ? "Saving…" : `Apply ${writes} ${writes === 1 ? "shape" : "shapes"}`}
          </button>
        )}
        <button type="button" data-testid="area-import-close" disabled={busy} onClick={onClose}>
          {step === "done" ? "Close" : "Cancel"}
        </button>
      </div>
      {step === "asking" && (
        <p className="confirm" data-testid="area-import-ask">
          Give {writes} {writes === 1 ? "area" : "areas"} the shape or the colour of the files? Each shape is then a shape that a person drew: the night job does not
          change it. Each old shape stays in the shape history.{" "}
          <button type="button" className="danger" data-testid="area-import-confirm" onClick={() => void send(files, false, useColour)}>
            Apply the shapes
          </button>{" "}
          <button
            type="button"
            data-testid="area-import-keep"
            autoFocus
            onClick={() => {
              setStep("checked");
              requestAnimationFrame(() => applyButton.current?.focus());
            }}
          >
            Cancel
          </button>
        </p>
      )}
    </section>
  );
}

/** A file name that breaks only after a "-", a "." or a "_", not in a word. */
export const fileBreaks = (name: string) =>
  name.split(/(?<=[-._])/).map((part, index) => (
    <Fragment key={index}>
      {index > 0 && <wbr />}
      {part}
    </Fragment>
  ));

/** The result of each area code of the files: file, code, result, corners and colour. */
export function ShapeImportTable({ items }: { items: api.ShapeImportItem[] }) {
  if (!items.length) return <p className="sub">The files have no shape.</p>;
  return (
    <div className="table-scroll">
      <table className="area-import-table" data-testid="area-import-table">
        <thead>
          <tr>
            <th scope="col">File</th>
            <th scope="col">Code</th>
            <th scope="col">Result</th>
            <th scope="col" className="num">
              Corners
            </th>
            <th scope="col">Colour</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={`${item.file}-${item.code}-${index}`} data-testid="area-import-row" data-result={item.result} data-code={item.code}>
              <td className="area-import-file-name">{fileBreaks(item.file)}</td>
              <td>{item.code || "—"}</td>
              <td>
                {RESULT_TEXT[item.result] ?? item.result}
                {item.problem && <span className={item.result === "bad_shape" ? "error" : "sub"}> · {item.problem}</span>}
                {item.warnings.map((warning) => (
                  <span key={warning} className="sub">
                    {" "}
                    · {warning}
                  </span>
                ))}
              </td>
              <td className="num">{cornersText(item)}</td>
              <td>
                {item.colour ? (
                  <>
                    <span className="pin-swatch" style={{ background: item.colour }} aria-hidden="true" /> {item.colour}
                  </>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
